import debounce from 'lodash/debounce';
import Vue from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import {
  onSnapshot,
  executeQuery,
  firestoreCollection,
  firestoreDocumentById,
  createNewId,
} from '../firebase/firestore';
import { types as userTypes } from './user';
import { types as analysesCaptureTypes } from './analysesCapture';
import { MUTATION_REGISTER_SNAPSHOT_LISTENER } from './constants';
import {
  prepareHeaderFromUserAnalyses,
  saveAnalysis,
  saveUserAnalysis,
  removeAnalysisFromPortfolio,
  createNewAnalysis,
  getOrCreateAnalysisForDocument,
  saveThreadComment,
  buildAclObject,
  saveThreadCommentVote,
} from './analysisHelper';

const MODULE_PREFIX = 'analyses';
const GETTER_IN_PROGRESS = 'inProgress';
const GETTER_SAVE_ANALYSIS_IN_PROGRESS = 'saveAnalysisInProgress';
const GETTER_CURRENT_ANALYSIS = 'currentAnalysis';
const GETTER_ANALYSIS_FOUND = 'analysisFound';
const GETTER_CURRENT_ANALYSIS_COMMENTS = 'currentAnalysisComments';
const GETTER_CURRENT_ANALYSIS_USER_ANALYSES = 'currentAnalysisUserAnalyses';
const GETTER_DOCUMENT_ANALYSES = 'documentAnalyses';
const MUTATION_SET_DOCUMENT_ANALYSES = 'SET_DOCUMENT_ANALYSES';
const MUTATION_SET_IN_PROGRESS = 'SET_IN_PROGRESS';
const MUTATION_SET_FULL_ANALYSIS = 'SET_FULL_ANALYSIS';
const MUTATION_FETCHED_ANALYSIS = 'FETCHED_ANALYSIS';
const MUTATION_UNSET_CURRENT_ANALYSIS = 'UNSET_CURRENT_ANALYSIS';
const MUTATION_START_SAVE_ANALYSIS = 'START_SAVE_ANALYSIS';
const MUTATION_ANALYSIS_FINISHED = 'ANALYSIS_FINISHED';
const MUTATION_ANALYSIS_FOUND = 'ANALYSIS_FOUND';
const MUTATION_SET_CURRENT_USER_ANALYSES = 'SET_CURRENT_USER_ANALYSES';
const MUTATION_SET_CURRENT_COMMENTS = 'SET_CURRENT_USER_COMMENTS';
const MUTATION_SET_THREAD_COMMENT_VOTES = 'SET_THREAD_COMMENT_VOTES';
const ACTION_FETCH_DOCUMENT_ANALYSES = 'fetchDocumentAnalyses';
const ACTION_ADD_USER_ANALYSIS_ELEMENT = 'addUserAnalysisElement';
const ACTION_ARCHIVE_USER_ANALYSIS_ELEMENT = 'archiveUserAnalysisElement';
const ACTION_UPDATE_USER_ANALYSIS_ELEMENT = 'updateUserAnalysisElement';
const ACTION_FETCH_ANALYSIS = 'fetchAnalysis';
const ACTION_GET_ANALYSIS = 'getAnalysis';
const ACTION_GET_USERS_ANALYSIS = 'getUsersAnalysis';
const ACTION_FETCH_ANALYSES_FOR_DOCUMENT = 'fetchAnalysesForDocument';
const ACTION_SAVE_ANALYSIS = 'saveAnalysis';
const ACTION_SAVE_USERS_ANALYSES = 'saveUsersAnalyses';
const ACTION_UPDATE_ANALYSIS = ACTION_SAVE_ANALYSIS; // 'updateAnalysis';
const ACTION_SAVE_THREAD_COMMENT = 'saveThreadComment';
const ACTION_TOGGLE_USER_COMMENT_UPVOTE = 'toggleUserCommentUpvote';
const ACTION_UPDATE_USER_ANALYSIS_RATING = 'updateUserAnalysisRating';
const ACTION_UPDATE_ANALYSIS_TAGS = 'updateAnalysisTags';
const ACTION_UPDATE_ANALYSIS_ACL = 'updateAnalysisAcl';
const ACTION_RESET_ANALYSIS = 'resetAnalysis';
const ACTION_ADD_USERS_TO_ANALYSIS = 'addUsersToAnalysis';

const initialState = {
  analysisFound: undefined,
  currentAnalysis: undefined,
  currentAnalysisUserAnalyses: null,
  currentAnalysisComments: [],
  fullAnalysis: undefined,
  inProgress: false,
  saveAnalysisInProgress: false,
  maxAnalysesPerSection: undefined,
  documentAnalyses: [],
};

const getters = {
  [GETTER_IN_PROGRESS]: state => state.inProgress,
  [GETTER_SAVE_ANALYSIS_IN_PROGRESS]: state => state.saveAnalysisInProgress,
  [GETTER_CURRENT_ANALYSIS]: state => state.fullAnalysis,
  [GETTER_ANALYSIS_FOUND]: state => state.analysisFound, // TODO see if still used
  [GETTER_CURRENT_ANALYSIS_COMMENTS]: state => state.currentAnalysisComments,
  [GETTER_CURRENT_ANALYSIS_USER_ANALYSES]: state => state.currentAnalysisUserAnalyses,
  [GETTER_DOCUMENT_ANALYSES]: state => state.documentAnalyses,
};

const mutations = {
  [MUTATION_SET_DOCUMENT_ANALYSES]: (state, analysis) => {
    const idx = state.documentAnalyses.findIndex(da => da.id === analysis.id);
    if (idx !== -1) {
      state.documentAnalyses.splice(idx, 1);
    }

    state.documentAnalyses.push(analysis);
  },
  [MUTATION_SET_IN_PROGRESS]: (state, inProgress) => {
    state.inProgress = inProgress;
  },
  [MUTATION_FETCHED_ANALYSIS]: (state, analysis) => {
    Vue.set(state, 'currentAnalysis', analysis);
  },
  [MUTATION_SET_FULL_ANALYSIS]: (state, analysis) => {
    state.fullAnalysis = analysis;
  },
  [MUTATION_UNSET_CURRENT_ANALYSIS]: state => {
    state.currentAnalysis = undefined;
  },
  [MUTATION_START_SAVE_ANALYSIS]: state => {
    state.saveAnalysisInProgress = true;
  },
  [MUTATION_ANALYSIS_FINISHED]: state => {
    state.saveAnalysisInProgress = false;
  },
  [MUTATION_ANALYSIS_FOUND]: (state, analysisId) => {
    state.analysisFound = analysisId;
  },
  [MUTATION_SET_CURRENT_USER_ANALYSES]: (state, userAnalyses) => {
    Vue.set(state, 'currentAnalysisUserAnalyses', userAnalyses);
  },
  [MUTATION_SET_CURRENT_COMMENTS]: (state, comments) => {
    state.currentAnalysisComments = comments;
  },
  [MUTATION_SET_THREAD_COMMENT_VOTES]: (state, { commentId, votes }) => {
    const commentIndex = state.currentAnalysisComments.findIndex(comment => comment.id === commentId);
    const totalUpVotes = votes.reduce((upVotes, vote) => (vote.upVoteValue ? upVotes + 1 : upVotes), 0);
    Vue.set(state.currentAnalysisComments[commentIndex], 'votes', votes);
    Vue.set(state.currentAnalysisComments[commentIndex], 'upVotes', totalUpVotes);
  },
};

async function setFullAnalysis(commit, userDetails, analysis, userAnalyses = [], comments = []) {
  commit(MUTATION_FETCHED_ANALYSIS, analysis);
  commit(MUTATION_SET_CURRENT_USER_ANALYSES, userAnalyses);
  commit(MUTATION_SET_CURRENT_COMMENTS, comments);

  const preparedAnalyses = await prepareHeaderFromUserAnalyses(
    userDetails.userId,
    userDetails.displayName,
    [{ ...analysis, userAnalyses, comments }],
  );

  // Defensive fix against bad leadImageUrl (should always start with a protocol)
  // We should fix the real thing tho!!
  const { leadImageUrl } = preparedAnalyses[0].documentDetails;

  if (leadImageUrl && leadImageUrl.startsWith('/img')) {
    console.warn('Fixing bad leadImageUrl (startsWith /img)');
    preparedAnalyses[0].documentDetails.leadImageUrl = undefined;
  }

  commit(MUTATION_SET_FULL_ANALYSIS, preparedAnalyses[0]);
}

export async function onAnalysisSnapshot({ commit, state, userDetails }, analysisSnapshot) {
  const analysisSnapshotData = analysisSnapshot.data();
  if (analysisSnapshotData) {
    commit(MUTATION_FETCHED_ANALYSIS, analysisSnapshotData);
    // Do not set fullAnalysis without userAnalyses
    if (state.currentAnalysisUserAnalyses) {
      // eslint-disable-next-line
      await setFullAnalysis(commit, userDetails, analysisSnapshotData, state.currentAnalysisUserAnalyses, state.currentAnalysisComments);
    }
  }
}

async function onUserAnalysisSnapshots({ commit, state, userDetails }, snapshots) {
  if (snapshots && snapshots.docs) {
    const userAnalyses = snapshots.docs.map(snapshot => snapshot.data());
    commit(MUTATION_SET_CURRENT_USER_ANALYSES, snapshots.docs.map(snapshot => snapshot.data()));

    // TODO there seems to be a race condition here between analysis, userAnalyses
    // it kind of works but setFullAnalysis could fail in userAnalyses if analysis header
    // is not loaded yet
    await setFullAnalysis(commit, userDetails, state.currentAnalysis, userAnalyses, state.currentAnalysisComments);
  }
}

async function onCommentSnapshots({ commit, state, userDetails }, snapshots) {
  if (snapshots && snapshots.docs) {
    const comments = snapshots.docs.map(snapshot => snapshot.data());
    //
    // Register snapshot listeners for the votes subcol of all comments
    comments.forEach((comment, index) => {
      const commentId = comment.id;
      const unsubscribeCommentVotes = onSnapshot(
        firestoreCollection(`analyses/${state.currentAnalysis.id}/analysisComments/${comment.id}/votes`),
        debounce(onAnalysisCommentVotesSnapshot.bind(this, { commit, commentId }), 1000),
        {
          retry: true,
        },
      );

      commit(
        MUTATION_REGISTER_SNAPSHOT_LISTENER,
        { snapshotListener: unsubscribeCommentVotes, id: `commentVotes-${index}` },
        { root: true },
      );
    });


    commit(MUTATION_SET_CURRENT_COMMENTS, comments);
    await setFullAnalysis(commit, userDetails, state.currentAnalysis, state.currentAnalysisUserAnalyses, comments);
  }
}

async function onAnalysisCommentVotesSnapshot({ commit, commentId }, snapshots) {
  if (snapshots && snapshots.docs) {
    const votes = await snapshots.docs.map(snapshot => snapshot.data());
    commit(MUTATION_SET_THREAD_COMMENT_VOTES, { commentId, votes });
  }
}

async function onDocumentAnalysesSnapshot({ commit }, snapshot) {
  if (snapshot && snapshot.exists) {
    const analysis = await snapshot.data();
    await commit(MUTATION_SET_DOCUMENT_ANALYSES, analysis);
  }
}

async function getAnalysesForDocumentIdAndUserId(documentId, userId) {
  const analysisAclList = await executeQuery(
    firestoreCollection('analysisAcl')
      .where('documentId', '==', documentId)
      .where('id', '==', userId),
  );
  if (analysisAclList && analysisAclList.length > 0) {
    return Promise.all(analysisAclList.map(acl => executeQuery(firestoreDocumentById('analyses', acl.analysisId))));
  }
  return [];
}

function showSuccessFeedback(dispatch) {
  dispatch(
    'handleOperationCompletion',
    {
      message: 'components.ExpressAnalysis.success',
      color: 'success',
    },
    {
      root: true,
    },
  );
}

function showErrorFeedback(dispatch) {
  dispatch(
    'handleOperationCompletion',
    {
      message: 'errors.generic',
      color: 'error',
    },
    { root: true },
  );
}

// TODO wrap args in an object
async function prepareAnalysis(rootGetters, commit, dispatch, userAnalysis, analysisModification) {
  const preparedAnalysis = rootGetters[analysesCaptureTypes.getters.preparedAnalysis];
  await dispatch(analysesCaptureTypes.actions.savePreparedAnalysis, analysisModification, { root: true });
  await commit(MUTATION_FETCHED_ANALYSIS, preparedAnalysis);
  if (userAnalysis) await commit(MUTATION_SET_CURRENT_USER_ANALYSES, [userAnalysis]);
  await dispatch(ACTION_FETCH_ANALYSIS, preparedAnalysis.id);
}

const actions = {
  [ACTION_FETCH_DOCUMENT_ANALYSES]: async ({ commit }, analysesToLoad) => {
    const unsubscribeAnalyses = [];

    analysesToLoad.forEach(analysisId => {
      const query = firestoreDocumentById(
        'analyses', analysisId,
      );

      // TODO unsubscribe when AnalysisSelector is destroyed
      unsubscribeAnalyses[analysisId] = onSnapshot(
        query,
        onDocumentAnalysesSnapshot.bind(this, { commit }),
        {
          retry: true,
        },
      );
    });
  },
  [ACTION_ARCHIVE_USER_ANALYSIS_ELEMENT]: async ({ state, dispatch }, { userAnalysis, element }) => {
    const userAnalysisCopy = cloneDeep(userAnalysis);

    let elementsArray;
    switch (element.type) {
      case 'highlight':
        elementsArray = userAnalysisCopy.evaluation.significantElements;
        break;

      case 'comment':
        elementsArray = userAnalysisCopy.reflection.comments;
        break;

      default:
        throw new Error('Unknown element type: ', element.type);
    }
    elementsArray.find(elem => elem.id === element.id).archived = true;

    await dispatch(ACTION_SAVE_USERS_ANALYSES, {
      analysis: state.currentAnalysis,
      userAnalysis: userAnalysisCopy,
    });
  },
  [ACTION_UPDATE_USER_ANALYSIS_ELEMENT]: async ({ state, dispatch }, { userAnalysis, element }) => {
    const userAnalysisCopy = cloneDeep(userAnalysis);

    let elementsArray;
    switch (element.type) {
      case 'highlight':
        elementsArray = userAnalysisCopy.evaluation.significantElements;
        break;

      case 'comment':
        elementsArray = userAnalysisCopy.reflection.comments;
        break;

      case 'mission':
        if (!userAnalysisCopy.missions) {
          userAnalysisCopy.missions = [];
        }
        elementsArray = userAnalysisCopy.missions;
        break;

      default:
        throw new Error('Unknown element type: ', element.type);
    }
    const elemToUpdateIndex = elementsArray.findIndex(elem => elem.id === element.id);
    elementsArray[elemToUpdateIndex] = { ...element };

    await dispatch(ACTION_SAVE_USERS_ANALYSES, {
      analysis: state.currentAnalysis,
      userAnalysis: userAnalysisCopy,
    });
  },
  // eslint-disable-next-line
  [ACTION_ADD_USER_ANALYSIS_ELEMENT]: async ({ state, commit, dispatch, rootGetters }, { element, userAnalysis }) => {
    // When coming here from the extension, the analysis may not exist yet. If this is the case,
    // we get the preparedAnalysis available in the analysesCapture store
    if (state.currentAnalysis === undefined) {
      await prepareAnalysis(rootGetters, commit, dispatch, userAnalysis);
    }

    const userAnalysisToUpdate = state.currentAnalysisUserAnalyses
      && state.currentAnalysisUserAnalyses.find(uAnalysis => uAnalysis.id === userAnalysis.id);
    if (!userAnalysisToUpdate) throw new Error('User analysis not found in state: ', userAnalysis.id);

    const userAnalysisCopy = cloneDeep(userAnalysisToUpdate);
    const newElement = {
      ...element,
      id: uuid(),
      timestamp: Date.now(),
    };

    switch (element.type) {
      case 'highlight':
        userAnalysisCopy.evaluation.significantElements.push(newElement);
        break;

      case 'comment':
        userAnalysisCopy.reflection.comments.push(newElement);
        break;

      case 'mission':
        if (!userAnalysisCopy.missions) {
          userAnalysisCopy.missions = [];
        }
        userAnalysisCopy.missions.push(newElement);
        break;

      default:
        throw new Error('Unknown element type: ', element.type);
    }

    await dispatch(ACTION_SAVE_USERS_ANALYSES, {
      analysis: state.currentAnalysis,
      userAnalysis: userAnalysisCopy,
    });
  },
  [ACTION_GET_ANALYSIS]: async (_, { analysisId, onSnapshotCallback }) => {
    const query = firestoreDocumentById(
      'analyses', analysisId,
    );
    const unsubscribeAnalysis = onSnapshot(
      query,
      onSnapshotCallback,
      { retry: true },
    );

    return unsubscribeAnalysis;
  },
  [ACTION_GET_USERS_ANALYSIS]: (_, { analysisId, onSnapshotCallback }) => {
    const unsubscribeUsersAnalysis = onSnapshot(
      firestoreCollection(`analyses/${analysisId}/userAnalyses`),
      onSnapshotCallback,
      { retry: true },
    );

    return unsubscribeUsersAnalysis;
  },
  [ACTION_FETCH_ANALYSIS]: async ({ commit, state, rootGetters }, analysisId) => {
    const userDetails = rootGetters[userTypes.getters.userDetails];
    const unsubscribeAnalysis = onSnapshot(
      firestoreDocumentById('analyses', analysisId),
      debounce(onAnalysisSnapshot.bind(this, { commit, state, userDetails }), 1000),
      { retry: true },
    );
    const unsubscribeUserAnalyses = onSnapshot(
      firestoreCollection(`analyses/${analysisId}/userAnalyses`),
      debounce(onUserAnalysisSnapshots.bind(this, { commit, state, userDetails }), 1000),
      { retry: true },
    );
    const unsubscribeComments = onSnapshot(
      firestoreCollection(`analyses/${analysisId}/analysisComments`),
      debounce(onCommentSnapshots.bind(this, { commit, state, userDetails }), 1000),
      { retry: true },
    );

    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribeAnalysis, id: 'analyses' }, { root: true },
    );
    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribeUserAnalyses, id: 'analyses.userAnalyses' },
      { root: true },
    );
    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER, { snapshotListener: unsubscribeComments, id: 'analyses.comments' },
      { root: true },
    );
  },
  // TODO check if still used
  [ACTION_FETCH_ANALYSES_FOR_DOCUMENT]: async ({ rootGetters, commit, dispatch }, documentId) => {
    const userDetails = rootGetters[userTypes.getters.userDetails];
    const analyses = await getAnalysesForDocumentIdAndUserId(documentId, userDetails.userId);
    if (analyses && analyses.length > 0) {
      if (analyses.length === 1) {
        commit(MUTATION_ANALYSIS_FOUND, analyses[0].id);
        dispatch(ACTION_FETCH_ANALYSIS, analyses[0].id);
      } else {
        // TODO: more than one, handle later (show a pop up and what not, actions won't be able to handle that...)
        // TODO: maybe we can populate a analysesCandidate field that the Vue will handle
        commit(MUTATION_ANALYSIS_FOUND, analyses[0].id);
        dispatch(ACTION_FETCH_ANALYSIS, analyses[0].id);
      }
    } else {
      const noosDocumentsPromise = executeQuery(
        firestoreCollection('noosDocuments')
          .where('documentId', '==', documentId),
      );

      const orgId = userDetails.organizationId;
      const orgJournalPromise = executeQuery(
        firestoreCollection(`organizations/${orgId}/journal`)
          .where('documentId', '==', documentId),
      );

      let docs = await noosDocumentsPromise;
      if (!Array.isArray(docs) || docs.length === 0) {
        docs = await orgJournalPromise;
      }

      if (docs && docs.length === 1) {
        const newAnalysis = createNewAnalysis(
          docs[0],
          userDetails.displayName,
          buildAclObject('User', userDetails.userId, userDetails.displayName),
        );
        await setFullAnalysis(commit, userDetails, newAnalysis, newAnalysis.userAnalyses);
      } else {
        // TODO: document is not accessible... throw?
        console.error('Document not found', documentId);
      }
    }
  },
  [ACTION_SAVE_USERS_ANALYSES]: async ({ dispatch, rootGetters }, { analysis, userAnalysis }) => {
    if (!analysis && !analysis.id) {
      throw new Error(
        'Can\'t save users analyses without an existing analysis.',
      );
    }

    const userDetails = rootGetters[userTypes.getters.userDetails];
    try {
      await saveUserAnalysis(userDetails.userId, analysis.id, userAnalysis);
      showSuccessFeedback(dispatch);
    } catch (error) {
      showErrorFeedback(dispatch);
      console.error('FAILURE: ACTION_SAVE_USERS_ANALYSES', error);
    }
  },
  [ACTION_SAVE_ANALYSIS]: async ({ commit, dispatch }, { analysis, portfolioIds }) => {
    if (!analysis && !analysis.id) {
      throw new Error(
        'Can\'t save analysis without an existing analysis.',
      );
    }

    commit(MUTATION_START_SAVE_ANALYSIS);
    try {
      await saveAnalysis(analysis.id, analysis, portfolioIds);
    } catch (error) {
      showErrorFeedback(dispatch);
      console.error('FAILURE: ACTION_SAVE_ANALYSIS', error);
    }
    commit(MUTATION_ANALYSIS_FINISHED);
  },
  [ACTION_RESET_ANALYSIS]: async ({ commit }) => {
    commit(MUTATION_FETCHED_ANALYSIS, undefined);
  },
  [ACTION_SAVE_THREAD_COMMENT]: async ({ dispatch }, { analysisId, comment }) => {
    try {
      if (!comment.id) {
        comment.id = createNewId();
        comment.timestamp = Date.now();
        comment.archived = false;
      }
      await saveThreadComment(analysisId, comment);
      showSuccessFeedback(dispatch);
    } catch (error) {
      showErrorFeedback(dispatch);
      console.error('FAILURE: ACTION_SAVE_THREAD_COMMENT', error);
    }
  },
  [ACTION_TOGGLE_USER_COMMENT_UPVOTE]: async ({ state }, { commentId, userId }) => {
    const commentIndex = state.currentAnalysisComments.findIndex(comment => comment.id === commentId);
    let userUpVoteOldValue = false;
    if (state.currentAnalysisComments[commentIndex].votes) {
      const userVoteIndex = state.currentAnalysisComments[commentIndex].votes.findIndex(vote => vote.id === userId);
      if (userVoteIndex !== -1) {
        userUpVoteOldValue = state.currentAnalysisComments[commentIndex].votes[userVoteIndex].upVoteValue;
      }
    }
    const userUpVoteNewValue = !userUpVoteOldValue;
    saveThreadCommentVote(state.currentAnalysis.id, commentId, userId, userUpVoteNewValue);
  },
  [ACTION_UPDATE_USER_ANALYSIS_RATING]: async ({ state, commit, dispatch, rootGetters }, { userAnalysis, rating }) => {
    const userAnalysisCopy = cloneDeep(userAnalysis);
    userAnalysisCopy.evaluation.documentScore = rating;

    if (state.currentAnalysis === undefined) {
      await prepareAnalysis(rootGetters, commit, dispatch, userAnalysis);
    }

    await dispatch(ACTION_SAVE_USERS_ANALYSES, {
      analysis: state.currentAnalysis,
      userAnalysis: userAnalysisCopy,
    });
  },
  [ACTION_UPDATE_ANALYSIS_TAGS]: async ({ state, commit, dispatch, rootGetters }, tags) => {
    if (state.currentAnalysis === undefined) {
      await prepareAnalysis(rootGetters, commit, dispatch, undefined, { tags });
    } else {
      const analysis = cloneDeep(state.currentAnalysis);
      analysis.tags = tags;

      await dispatch(ACTION_SAVE_ANALYSIS, { analysis });
    }
  },
  [ACTION_ADD_USERS_TO_ANALYSIS]: async ({ state, commit, dispatch, rootGetters }, users) => {
    if (state.currentAnalysis === undefined) {
      await prepareAnalysis(rootGetters, commit, dispatch, undefined);
    }

    const analysis = cloneDeep(state.currentAnalysis);
    users.forEach(user => {
      const alreadyHasAccess = analysis.acl.some(a => a.entityId === user.id);
      if (!alreadyHasAccess) {
        const aclObject = buildAclObject('User', user.id, `${user.firstName} ${user.lastName}`);
        analysis.acl.push(aclObject);
      }
    });

    await dispatch(ACTION_SAVE_ANALYSIS, { analysis });
  },
  [ACTION_UPDATE_ANALYSIS_ACL]: async ({ state, commit, dispatch, rootGetters }, {
    acl,
    portfolioIdsToAdd,
    portfolioIdsToRemove,
  }) => {
    // Step 0: Make sure the current analysis is the right one and create the analysis if not
    // present in the extension.
    if (state.currentAnalysis === undefined) {
      await prepareAnalysis(rootGetters, commit, dispatch, undefined, { acl });
    }

    const analysis = cloneDeep(state.currentAnalysis);

    // Step 1: Update ACL
    analysis.acl = acl;
    await dispatch(ACTION_SAVE_ANALYSIS, { analysis, portfolioIds: portfolioIdsToAdd });

    // Step 2: Remove analysis to portfolios
    // eslint-disable-next-line
    for (const id of portfolioIdsToRemove) {
      // eslint-disable-next-line
      await removeAnalysisFromPortfolio(analysis.id, id);
    }
  },
};

export const getAccessibleAnalysisIdForDocument = async (
  document,
  userId,
  userDisplayName,
  ownerType,
  ownerId,
  ownerName,
) => {
  const isDocument = document.documentDetails.documentType === 'document';
  const aclObject = buildAclObject(ownerType, ownerId, ownerName);
  let analysisId;

  if (isDocument) {
    const { analysis } = await getOrCreateAnalysisForDocument(
      document, userId, userDisplayName, aclObject,
    );
    // TODO handle multiple analyses for document
    analysisId = analysis.id;
  } else {
    analysisId = document.id;
  }

  return analysisId;
};


export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export const types = {
  getters: {
    analysisFound: `${MODULE_PREFIX}/${GETTER_ANALYSIS_FOUND}`,
    currentAnalysis: `${MODULE_PREFIX}/${GETTER_CURRENT_ANALYSIS}`,
    currentAnalysisComments: `${MODULE_PREFIX}/${GETTER_CURRENT_ANALYSIS_COMMENTS}`,
    currentAnalysisUserAnalyses: `${MODULE_PREFIX}/${GETTER_CURRENT_ANALYSIS_USER_ANALYSES}`,
    inProgress: `${MODULE_PREFIX}/${GETTER_IN_PROGRESS}`,
    saveAnalysisInProgress: `${MODULE_PREFIX}/${GETTER_SAVE_ANALYSIS_IN_PROGRESS}`,
    documentAnalyses: `${MODULE_PREFIX}/${GETTER_DOCUMENT_ANALYSES}`,
  },
  mutations: {
    ANALYSIS_FINISHED: `${MODULE_PREFIX}/${MUTATION_ANALYSIS_FINISHED}`,
    FETCHED_ANALYSIS: `${MODULE_PREFIX}/${MUTATION_FETCHED_ANALYSIS}`,
    START_SAVE_ANALYSIS: `${MODULE_PREFIX}/${MUTATION_START_SAVE_ANALYSIS}`,
    UNSET_CURRENT_ANALYSIS: `${MODULE_PREFIX}/${MUTATION_UNSET_CURRENT_ANALYSIS}`,
  },
  actions: {
    fetchDocumentAnalyses: `${MODULE_PREFIX}/${ACTION_FETCH_DOCUMENT_ANALYSES}`,
    addUserAnalysisElement: `${MODULE_PREFIX}/${ACTION_ADD_USER_ANALYSIS_ELEMENT}`,
    archiveUserAnalysisElement: `${MODULE_PREFIX}/${ACTION_ARCHIVE_USER_ANALYSIS_ELEMENT}`,
    updateUserAnalysisElement: `${MODULE_PREFIX}/${ACTION_UPDATE_USER_ANALYSIS_ELEMENT}`,
    fetchAnalysis: `${MODULE_PREFIX}/${ACTION_FETCH_ANALYSIS}`,
    fetchAnalysesForDocument: `${MODULE_PREFIX}/${ACTION_FETCH_ANALYSES_FOR_DOCUMENT}`,
    getAnalysis: `${MODULE_PREFIX}/${ACTION_GET_ANALYSIS}`,
    resetAnalysis: `${MODULE_PREFIX}/${ACTION_RESET_ANALYSIS}`,
    getUsersAnalysis: `${MODULE_PREFIX}/${ACTION_GET_USERS_ANALYSIS}`,
    saveUsersAnalyses: `${MODULE_PREFIX}/${ACTION_SAVE_USERS_ANALYSES}`,
    updateAnalysis: `${MODULE_PREFIX}/${ACTION_UPDATE_ANALYSIS}`,
    saveThreadComment: `${MODULE_PREFIX}/${ACTION_SAVE_THREAD_COMMENT}`,
    toggleUserCommentUpvote: `${MODULE_PREFIX}/${ACTION_TOGGLE_USER_COMMENT_UPVOTE}`,
    updateUserAnalysisRating: `${MODULE_PREFIX}/${ACTION_UPDATE_USER_ANALYSIS_RATING}`,
    updateAnalysisTags: `${MODULE_PREFIX}/${ACTION_UPDATE_ANALYSIS_TAGS}`,
    updateAnalysisAcl: `${MODULE_PREFIX}/${ACTION_UPDATE_ANALYSIS_ACL}`,
    addUsersToAnalysis: `${MODULE_PREFIX}/${ACTION_ADD_USERS_TO_ANALYSIS}`,
  },
};
